import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/brand/brand/apps/docs/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`Primer Brand defines various global styling defaults using CSS variables, while also allowing app-level customization through its `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{`.`}</p>
    <p>{`CSS variables are available for most styling dimensions such as typography, color and sizing. They are defined in the primary stylesheet: `}<inlineCode parentName="p">{`main.css`}</inlineCode>{`.`}</p>
    <p><inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` extends theming capabilities at runtime by enabling real-time color mode switching among other things.`}</p>
    <p>{`Using the CSS stylesheet and `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` in tandem is crucial to make theming work correctly.`}</p>
    <h2>{`Configure the `}<inlineCode parentName="h2">{`ThemeProvider`}</inlineCode></h2>
    <p>{`The `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` should wrap your React application root to enable theming correctly. It can also be nested to apply isolated behavior to `}<inlineCode parentName="p">{`children`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import {ThemeProvider} from '@primer/react-brand'

function App() {
  return (
    <ThemeProvider>
      <div>...</div>
    </ThemeProvider>
  )
}
`}</code></pre>
    <h2>{`Load the stylesheet`}</h2>
    <p>{`The stylesheet contains all of the required rules for design tokens and components. It also includes a CSS reset, so we recommend loading this as early as possible in your HTML `}<inlineCode parentName="p">{`<head>`}</inlineCode>{` to minimize undesirable rendering behavior.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import '@primer/react-brand/lib/css/main.css'
`}</code></pre>
    <h2>{`Set a color mode`}</h2>
    <p>{`Primer Brand supports the following color modes through a dedicated prop by applying HTML `}<inlineCode parentName="p">{`data-*`}</inlineCode>{` attributes:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Theme`}</th>
          <th parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="th">{`colorMode`}</inlineCode>{` prop value`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Data attribute applied`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Light`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`light`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`data-color-mode="light"`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Dark`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`dark`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`data-color-mode="dark"`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p>{`The `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` applies the data attributes and handles state-changes internally.`}</p>
    <p><strong parentName="p"><inlineCode parentName="strong">{`ThemeProvider`}</inlineCode>{` will apply `}<inlineCode parentName="strong">{`light`}</inlineCode>{` mode by default if the `}<inlineCode parentName="strong">{`colorMode`}</inlineCode>{` is not specified. This is recommended for top-level placement.`}</strong></p>
    <p>{`The following example demonstrates how `}<inlineCode parentName="p">{`light`}</inlineCode>{` mode will enable by default, and can also be changed to `}<inlineCode parentName="p">{`dark`}</inlineCode>{` declaratively:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const Example = () => {
  const [colorMode, setColorMode] = React.useState()

  const handleChange = (event) => {
    event.preventDefault()
    setColorMode('dark')
  }

  return (
    <ThemeProvider
      colorMode={colorMode}
      style={{backgroundColor: 'var(--brand-color-canvas-default)'}}
    >
      <River>
        <River.Visual>
          <img
            src="https://via.placeholder.com/600x400/f5f5f5/f5f5f5.png"
            alt="placeholder, blank area with an off-white background color"
          />
        </River.Visual>
        <River.Content>
          <Heading>Heading</Heading>
          <Text>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. In sapien
            sit ullamcorper id. Aliquam luctus sed turpis felis nam pulvinar
            risus elementum.
          </Text>
          <Link href="#" onClick={handleChange}>
            {colorMode === 'dark'
              ? "I'm in dark mode now"
              : 'Switch to dark mode'}
          </Link>
        </River.Content>
      </River>
    </ThemeProvider>
  )
}

render(Example)
`}</code></pre>
    <h3>{`Locking the color mode`}</h3>
    <p>{`For tighter control over appearance - such as forcing areas of a layout to appear in dark mode - we recommend nesting `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` and explicitly applying a color mode.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<>
  <ThemeProvider
    colorMode="dark"
    style={{
      backgroundColor: 'var(--brand-color-canvas-default)',
    }}
  >
    <River align="center">
      <River.Visual>
        <img
          src="https://via.placeholder.com/600x400/f5f5f5/f5f5f5.png"
          alt="placeholder, blank area with an off-white background color"
        />
      </River.Visual>
      <River.Content>
        <Heading>Heading</Heading>
        <Text>
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. In sapien sit
          ullamcorper id. Aliquam luctus sed turpis felis nam pulvinar risus
          elementum.
        </Text>
        <Link href="#">Call to action</Link>
      </River.Content>
    </River>
  </ThemeProvider>
  <ThemeProvider
    colorMode="light"
    style={{
      backgroundColor: 'var(--brand-color-canvas-default)',
    }}
  >
    <Container>
      <River align="right">
        <River.Visual>
          <img
            src="https://via.placeholder.com/600x400/f5f5f5/f5f5f5.png"
            alt="placeholder, blank area with an off-white background color"
          />
        </River.Visual>
        <River.Content>
          <Heading>Heading</Heading>
          <Text>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. In sapien
            sit ullamcorper id. Aliquam luctus sed turpis felis nam pulvinar
            risus elementum.
          </Text>
          <Link href="#">Call to action</Link>
        </River.Content>
      </River>
    </Container>
  </ThemeProvider>
</>
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      